import { Controller } from '@hotwired/stimulus'
import LineChart from '~/javascript/charts/d3/LineChart'
import BarChart from '~/javascript/charts/d3/BarChart'
import HeatmapChart from '~/javascript/charts/d3/HeatmapChart'
import RadarChart from '~/javascript/charts/d3/RadarChart'
import StackedAreaChart from '~/javascript/charts/d3/StackedAreaChart'
import HorizonChart from '~/javascript/charts/d3/HorizonChart'
import HexbinChart from '~/javascript/charts/d3/HexbinChart'
import StackedNormalizedBarChart from '~/javascript/charts/d3/StackedNormalizedBarChart'
import StackedGroupedBarChart from '~/javascript/charts/d3/StackedGroupedBarChart'

export default class extends Controller {
  static targets = ['chart', 'download', 'embed', 'embedCode']

  static values = {
    type: { type: String, default: 'line' },
    config: { type: Object, default: {} },
    title: { type: String, default: '' },
    apiUrl: { type: String, default: '' },
    embedUrl: { type: String, default: '' }
  }

  // Parse data manually since it can be Object or Array
  get chartData() {
    // Return loaded data if available (from API)
    if (this._loadedData) return this._loadedData

    // Otherwise parse from data attribute
    // Key has hyphen due to controller namespace: data-data--visualization-data-value
    const dataAttr = this.element.dataset['data-VisualizationDataValue']
    if (!dataAttr) return null
    try {
      return JSON.parse(dataAttr)
    } catch (e) {
      console.error('Failed to parse chart data:', e)
      return null
    }
  }

  connect() {
    this._loadedData = null
    console.log('Visualization controller connected', {
      type: this.typeValue,
      hasChartTarget: this.hasChartTarget,
      hasData: !!this.chartData,
      dataType: Array.isArray(this.chartData) ? 'array' : typeof this.chartData,
      datasetKeys: Object.keys(this.element.dataset)
    })
    this.chart = null
    this.initChart()

    // Load data from API if provided
    if (this.apiUrlValue) {
      this.loadData()
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  async loadData() {
    try {
      const response = await fetch(this.apiUrlValue, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) throw new Error('Failed to load data')

      const result = await response.json()
      this._loadedData = result.data || result
      this.initChart()
    } catch (error) {
      console.error('Failed to load visualization data:', error)
      this.showError()
    }
  }

  initChart() {
    if (!this.hasChartTarget) return

    const rawData = this.chartData
    if (!rawData || (Array.isArray(rawData) && rawData.length === 0) ||
        (typeof rawData === 'object' && !Array.isArray(rawData) && Object.keys(rawData).length === 0)) {
      return
    }

    // Destroy existing chart
    if (this.chart) {
      this.chart.destroy()
    }

    const options = {
      width: 800,
      height: 500,
      animate: true,
      responsive: true,
      ...this.configValue
    }

    // Get appropriate data format for each chart type
    let chartData
    try {
      switch (this.typeValue) {
        case 'line':
          chartData = rawData.values || rawData
          this.chart = new LineChart(this.chartTarget, options)
          break
        case 'area':
          chartData = rawData.values || rawData
          this.chart = new LineChart(this.chartTarget, { ...options, showArea: true })
          break
        case 'bar':
          chartData = rawData.values || rawData
          this.chart = new BarChart(this.chartTarget, options)
          break
        case 'heatmap':
          chartData = rawData.values || rawData
          this.chart = new HeatmapChart(this.chartTarget, options)
          break
        case 'radar':
          chartData = rawData.values || rawData
          this.chart = new RadarChart(this.chartTarget, options)
          break
        case 'stacked-area':
        // Expects { series: [...], data: [...] }
          chartData = rawData
          this.chart = new StackedAreaChart(this.chartTarget, options)
          break
        case 'horizon':
        // Expects { series: [...] } or array of series
          chartData = rawData.series || rawData
          this.chart = new HorizonChart(this.chartTarget, options)
          break
        case 'hexbin':
        // Expects array of { x, y } points
          chartData = rawData.points || rawData
          this.chart = new HexbinChart(this.chartTarget, options)
          break
        case 'stacked-normalized-bar':
        // Expects array of { category, segments: [...] }
          chartData = Array.isArray(rawData) ? rawData : rawData.values || rawData
          this.chart = new StackedNormalizedBarChart(this.chartTarget, options)
          break
        case 'stacked-grouped-bar':
        // Expects { categories: [...], groups: [...] }
          chartData = rawData
          this.chart = new StackedGroupedBarChart(this.chartTarget, options)
          break
        default:
          console.warn(`Unknown chart type: ${this.typeValue}`)
          return
      }

      if (!chartData || (Array.isArray(chartData) && chartData.length === 0)) {
        return
      }

      console.log(`Rendering ${this.typeValue} chart with data:`, chartData)
      this.chart.setData(chartData).render()
      console.log(`Successfully rendered ${this.typeValue} chart`)
    } catch (error) {
      console.error(`Failed to render ${this.typeValue} chart:`, error)
      this.showError()
    }
  }

  configValueChanged() {
    this.initChart()
  }

  // Download chart as PNG
  download(event) {
    event.preventDefault()

    const svg = this.chartTarget.querySelector('svg')
    if (!svg) return

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const svgData = new XMLSerializer().serializeToString(svg)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      // Set canvas size with higher resolution for quality
      const scale = 2
      canvas.width = img.width * scale
      canvas.height = img.height * scale
      ctx.scale(scale, scale)

      // White background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Draw image
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)

      // Download
      const link = document.createElement('a')
      const filename = this.titleValue ?
        `${this.titleValue.toLowerCase().replace(/\s+/g, '-')}.png` :
        'visualization.png'
      link.download = filename
      link.href = canvas.toDataURL('image/png')
      link.click()
    }
    img.src = url
  }

  // Download as SVG
  downloadSvg(event) {
    event.preventDefault()

    const svg = this.chartTarget.querySelector('svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    const filename = this.titleValue ?
      `${this.titleValue.toLowerCase().replace(/\s+/g, '-')}.svg` :
      'visualization.svg'
    link.download = filename
    link.href = url
    link.click()

    URL.revokeObjectURL(url)
  }

  // Toggle embed code display
  toggleEmbed(event) {
    event.preventDefault()

    if (this.hasEmbedTarget) {
      this.embedTarget.classList.toggle('hidden')
    }
  }

  // Copy embed code to clipboard
  copyEmbed(event) {
    event.preventDefault()

    if (!this.hasEmbedCodeTarget) return

    const embedCode = this.embedCodeTarget.textContent || this.embedCodeTarget.value

    navigator.clipboard.writeText(embedCode).then(() => {
      const button = event.currentTarget
      const originalText = button.textContent
      button.textContent = 'COPIED!'
      setTimeout(() => {
        button.textContent = originalText
      }, 2000)
    }).catch((err) => {
      console.error('Failed to copy embed code:', err)
    })
  }

  // Share to Pinterest
  sharePinterest(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.shareUrl
    if (url) {
      window.open(url, 'pinterest-share', 'width=750,height=500')
    }
  }

  // Share to Twitter
  shareTwitter(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.shareUrl
    if (url) {
      window.open(url, 'twitter-share', 'width=550,height=450')
    }
  }

  // Share to Facebook
  shareFacebook(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.shareUrl
    if (url) {
      window.open(url, 'facebook-share', 'width=580,height=400')
    }
  }

  // Share to LinkedIn
  shareLinkedin(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.shareUrl
    if (url) {
      window.open(url, 'linkedin-share', 'width=600,height=500')
    }
  }

  showError() {
    if (this.hasChartTarget) {
      this.chartTarget.innerHTML = `
        <div class="d3-error-state" style="
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          border: 2px dashed #d4d4d4;
          color: #737373;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
        ">
          FAILED TO LOAD VISUALIZATION
        </div>
      `
    }
  }
}
